import { Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface CampaignConversionEmailControlProps {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  translationPrefix: string;
}

/**
 * Render the campaign-level switch for standard conversion email delivery.
 *
 * @param checked - Whether automatic conversion emails are enabled.
 * @param onChange - Optional edit callback. When absent, the checkbox is read-only.
 * @param translationPrefix - Locale key prefix for the surrounding campaign form.
 * @returns A translated checkbox with explicit operational help copy.
 */
export default function CampaignConversionEmailControl({
  checked,
  onChange,
  translationPrefix,
}: CampaignConversionEmailControlProps) {
  const { t } = useTranslation(['pages']);
  const labelKey = `${translationPrefix}.automaticConversionEmails`;
  const tooltipKey = `${translationPrefix}.automaticConversionEmailsTooltip`;
  const helperKey = `${translationPrefix}.automaticConversionEmailsHelper`;

  return (
    <Box>
      <FormControlLabel
        control={(
          <Checkbox
            checked={checked}
            disabled={!onChange}
            onChange={(event) => onChange?.(event.target.checked)}
          />
        )}
        label={(
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span">{t(labelKey)}</Typography>
            <Tooltip title={t(tooltipKey)} arrow>
              <HelpOutline
                sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
              />
            </Tooltip>
          </Box>
        )}
      />
      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
        {t(helperKey)}
      </Typography>
    </Box>
  );
}
